import { Component, input, signal, resource, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { ExceptionInstance } from 'src/app/error-handling/models/exception-instance';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { DatePipe, DOCUMENT } from '@angular/common';
import { ExceptionType } from 'src/app/error-handling/models';
import { TrackingService } from '../services/tracking.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ExceptionInstanceInterpretation } from 'src/app/error-handling/models/exception-instance-interpretation';

type AIVoteState = null | 'pending' | 'up' | 'down';

@Component({
    selector: 'ep-tracking-error-box',
    templateUrl: './tracking-error-box.component.html',
    styleUrl: './tracking-error-box.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, DatePipe, NgOptimizedImage]
})
export class TrackingErrorBoxComponent {
    private trackingService = inject(TrackingService);
    private loadingPanelService = inject(LoadingPanelService);
    private readonly document = inject(DOCUMENT);
    
    exceptionInstance = input<ExceptionInstance>(null);
    errorType = input<string>(null);

    exceptionInstanceResource = resource({
        params: () => ({ exceptionInstance: this.exceptionInstance(), exceptionTypeError: this.exceptionTypeError() }),
        loader: ({ params }) => this.createExceptionTypeResult(params.exceptionInstance, params.exceptionTypeError)
    });
    errorTypeResource = resource({
        params: () => ({ errorType: this.errorType() }),
        loader: ({ params }) => this.setErrorType(params.errorType)
    });
    hasAiAccessResource = resource({
        params: () => ({}),
        loader: () => this.trackingService.hasAIAccess()
    });

    showDetails = signal<boolean>(false);
    isError = signal<boolean>(false);
    isWarning = signal<boolean>(false);
    exceptionTypeError = signal<string>('Unknown - error code has been deleted');
    exceptionInstanceInterpretation = signal<ExceptionInstanceInterpretation>(null);
    aiVoteState = signal<AIVoteState>(null);
    voteError = signal<string | null>(null);
    documentUrl = signal<string>(this.document.location.href);
    
    exceptionInstanceResult = computed(() => this.exceptionInstanceResource.value());
    hasAiAccess = computed(() => this.hasAiAccessResource.value());

    toggleShowDetails() {
        this.showDetails.set(!this.showDetails());
    }

    async requestAiInterpret(reCreateAIInterpretation: boolean = false): Promise<void> {
        this.loadingPanelService.showLoadingPanelDirect('Requesting AI interpretation');
        const exceptionInstanceId = this.exceptionInstance()?.ExceptionInstancesId || 0;
        const response = await this.trackingService.getAIErrorInterpretation(exceptionInstanceId, reCreateAIInterpretation);
        this.exceptionInstanceInterpretation.set(response);
        this.loadingPanelService.hideLoadingPanel();
    }

    async submitAiInterpretationVote(vote: AIVoteState): Promise<void> {
        const exception = this.exceptionInstanceResult();
        const ai = this.exceptionInstanceInterpretation();

        if (!exception || !ai) return;
        if (this.aiVoteState() === 'pending') return;
        if (this.aiVoteState() === vote) return;

        const previousVote = this.aiVoteState();
        this.aiVoteState.set('pending');
        this.voteError.set(null);

        try {
            await this.trackingService.voteAIInterpretation({
                isHelpful: vote === 'up',
                documentURL: this.documentUrl(),
                exceptionInstance: exception,
                exceptionInstanceInterpretation: ai
            });
            this.aiVoteState.set(vote);
        } catch {
            this.aiVoteState.set(previousVote);
            this.voteError.set('Unable to submit your feedback. Please try again.');
        }
    }

    private setErrorType(errorType: string): Promise<void> { 
        return new Promise((resolve) => {
            const e = errorType?.toLowerCase();
            if (e === 'error') {
                this.isError.set(true);
            } else if (e === 'warning') {
                this.isWarning.set(true);
            } else {
                this.isError.set(true);
            }
            resolve();
        });
    }

    private createExceptionTypeResult(exceptionInstance: ExceptionInstance, exceptionTypeError: string): Promise<ExceptionInstance> {
        return new Promise((resolve) => {
            if (exceptionInstance?.ExceptionType === null) {
                exceptionInstance.ExceptionType = {
                    ErrorCode: exceptionTypeError,
                    TechnicalDescription: exceptionTypeError,
                    ExceptionCause: exceptionTypeError,
                    ExceptionSolution: exceptionTypeError,
                    Area: exceptionTypeError,
                    Component: exceptionTypeError,
                    ExceptionCategory: {
                        ExceptionCategoryName: exceptionTypeError,
                        Description: exceptionTypeError,
                        ExceptionCategoriesId: 0,
                        ExceptionTypes: []
                    }
                } as ExceptionType;
            }
            resolve(exceptionInstance);
        });
    }
}
